import { SandboxDomainError } from '../errors';
import {
  CreatePaymentInput,
  SandboxClient,
  SandboxEvent,
  SandboxPayment,
  SandboxSession,
  SandboxState,
  SandboxWorker,
} from '../types';
import { startSessionAction } from './sessions';

export interface ProcessPostPaymentInput {
  method?: string;
  cash_sum?: number;
  comment?: string;
  confirm_pass?: string;
}

const requireClient = (state: SandboxState, uuid?: string): SandboxClient => {
  const client = state.clients.find((item) => item.uuid === uuid);
  if (!client) throw new SandboxDomainError('Клиент не найден');
  return client;
};

const requireGood = (
  state: SandboxState,
  id: number
): SandboxState['goods'][number] => {
  const good = state.goods.find((item) => item.id === id);
  if (!good) throw new SandboxDomainError('Товар не найден');
  return good;
};

const requireCombo = (
  state: SandboxState,
  id: number
): SandboxState['combos'][number] => {
  const combo = state.combos.find((item) => item.id === id);
  if (!combo) throw new SandboxDomainError('Комбо не найдено');
  return combo;
};

const eventClient = (client: SandboxClient): SandboxEvent['client'] => ({
  uuid: client.uuid,
  phone: client.phone,
  nickname: client.nickname,
  first_name: client.first_name,
  last_name: client.last_name,
});

const paymentItemsForEvent = (
  payment: SandboxPayment
): NonNullable<SandboxEvent['payment_items']> =>
  payment.items.map((item) => ({
    id: item.id,
    entity_type: item.type,
    title: item.entity?.title ?? item.type,
    amount: item.amount,
    sum: item.sum,
  }));

const sessionStartedEvent = (
  state: SandboxState,
  session: SandboxSession,
  timestamp: string,
  worker: SandboxWorker
): SandboxEvent => {
  const client = session.client_uuid
    ? state.clients.find((item) => item.uuid === session.client_uuid)
    : undefined;
  const host = state.hosts.find((item) => item.id === session.host_id);
  const expiresAt = session.duration
    ? new Date(
        Date.parse(session.started_at) + session.duration * 1000
      ).toISOString()
    : null;
  return {
    timestamp,
    type: 'CLIENT_SESSION_STARTED',
    description: 'Сеанс запущен',
    additional_data: null,
    additional_seconds: null,
    client: client ? eventClient(client) : null,
    worker,
    host: { title: host?.alias ?? `Host ${session.host_id}` },
    client_session: {
      elapsed: session.elapsed,
      expires_at: expiresAt,
      finished_at: session.finished_at,
      id: session.id,
      time_left: session.time_left,
      is_per_minute:
        session.postpaid || session.seances[0]?.tariff.kind === 'PER_MINUTE',
      total_cost: session.total_cost,
    },
  };
};

const entityFor = (
  state: SandboxState,
  type: string,
  id?: number
): SandboxPayment['items'][number]['entity'] => {
  if (!id) return undefined;
  if (type === 'GOOD') {
    const good = state.goods.find((item) => item.id === id);
    return good
      ? {
          __typename: 'Good',
          id: good.id,
          title: good.title,
          cost: good.cost,
          amount: good.amount,
          vat: good.vat,
          tax_system: good.tax_system,
        }
      : undefined;
  }
  if (type === 'SERVICE') {
    const service = state.services.find((item) => item.id === id);
    return service
      ? {
          __typename: 'Service',
          id: service.id,
          title: service.title,
          cost: service.cost,
          vat: service.vat,
          tax_system: service.tax_system,
        }
      : undefined;
  }
  if (type === 'TARIFF') {
    const tariff = state.tariffs.find((item) => item.id === id);
    return tariff
      ? {
          __typename: 'Tariff',
          id: tariff.id,
          title: tariff.title,
          cost: tariff.cost,
          disable_sale_receipt_printing: tariff.disable_sale_receipt_printing,
          per_minute: tariff.per_minute,
        }
      : undefined;
  }
  const combo = state.combos.find((item) => item.id === id);
  return type === 'COMBO' && combo
    ? {
        __typename: 'Combo',
        id: combo.id,
        title: combo.title,
        cost: combo.cost,
      }
    : undefined;
};

const allocateLineTotal = (total: number, weights: number[]): number[] => {
  if (!weights.length) return [];
  const safeTotal = Math.max(0, Number(total));
  const safeWeights = weights.map((weight) =>
    Number.isFinite(weight) && weight > 0 ? weight : 0
  );
  const weightTotal = safeWeights.reduce((sum, weight) => sum + weight, 0);
  let allocated = 0;
  return safeWeights.map((weight, index) => {
    if (index === safeWeights.length - 1) {
      return Math.max(0, Number((safeTotal - allocated).toFixed(10)));
    }
    const ratio = weightTotal > 0 ? weight / weightTotal : 1 / weights.length;
    const value = Math.min(
      safeTotal - allocated,
      Math.max(0, Math.round(safeTotal * ratio * 100) / 100)
    );
    allocated += value;
    return value;
  });
};

const materializePaymentItems = (
  state: SandboxState,
  input: CreatePaymentInput,
  paymentId: number
): SandboxPayment['items'] => {
  let nextItemId = paymentId * 1000;
  return input.items.flatMap((line) => {
    if (line.type !== 'COMBO') {
      return [
        {
          id: nextItemId++,
          amount: line.amount,
          type: line.type,
          sum: Number(line.sum),
          entity_id: line.entity_id,
          host_id: line.host_id,
          entity: entityFor(state, line.type, line.entity_id) ?? null,
        },
      ];
    }

    const combo = requireCombo(state, Number(line.entity_id));
    const components: Array<{
      amount: number;
      type: 'GOOD' | 'SERVICE' | 'TARIFF';
      entity_id: number;
      host_id?: number;
      weight: number;
    }> = combo.items.map((part) => {
      const amount = part.amount * line.amount;
      return {
        amount,
        type: part.entity_type,
        entity_id: part.entity.id,
        weight: part.price * amount,
      };
    });
    if (combo.tariff) {
      components.push({
        amount: line.amount,
        type: 'TARIFF',
        entity_id: combo.tariff.id,
        host_id: line.host_id,
        weight: combo.tariff.cost * line.amount,
      });
    }
    if (!components.length) {
      throw new SandboxDomainError('Комбо не содержит позиций');
    }
    const sums = allocateLineTotal(
      Number(line.sum),
      components.map((component) => component.weight)
    );
    return components.map((component, index) => ({
      id: nextItemId++,
      amount: component.amount,
      type: component.type,
      sum: sums[index],
      entity_id: component.entity_id,
      host_id: component.host_id,
      entity:
        entityFor(state, component.type, component.entity_id) ??
        (() => {
          throw new SandboxDomainError('Позиция комбо не найдена');
        })(),
    }));
  });
};

const paymentItemsForInput = (
  state: SandboxState,
  input: CreatePaymentInput,
  paymentId: number
): NonNullable<SandboxEvent['payment_items']> =>
  input.items.map((item, index) => ({
    id: paymentId * 100000 + index,
    entity_type: item.type,
    title: entityFor(state, item.type, item.entity_id)?.title ?? item.type,
    amount: item.amount,
    sum: Number(item.sum),
  }));

type TariffPaymentItem = SandboxPayment['items'][number] & {
  entity_id: number;
  host_id: number;
};

const startPaidTariffSession = (
  state: SandboxState,
  items: TariffPaymentItem[],
  client: SandboxClient | null,
  paymentId: number,
  timestamp: string
): SandboxSession => {
  const tariffs = items.map((item) => {
    const tariff = state.tariffs.find(
      (candidate) => candidate.id === item.entity_id
    );
    if (!tariff) throw new SandboxDomainError('Тариф не найден');
    return { item, tariff };
  });
  const first = tariffs[0];
  if (!first) throw new SandboxDomainError('Тариф не найден');

  const session = startSessionAction(
    state,
    {
      host_id: first.item.host_id,
      client_uuid: client?.uuid,
      tariff_id: first.tariff.id,
      payment_id: paymentId,
    },
    timestamp
  );
  const duration = tariffs.reduce(
    (sum, { item, tariff }) => sum + tariff.duration * item.amount,
    0
  );
  const hasPerMinuteTariff = tariffs.some(({ tariff }) => tariff.per_minute);
  const firstSeanceId = session.seances[0].id;

  session.duration = duration;
  session.time_left = duration;
  session.total_cost = items.reduce((sum, item) => sum + item.sum, 0);
  session.finished_at = hasPerMinuteTariff
    ? null
    : new Date(Date.parse(timestamp) + duration * 1000).toISOString();
  session.seances = tariffs.map(({ tariff }, index) => ({
    id: firstSeanceId + index,
    status: 'ACTIVE',
    tariff: {
      id: tariff.id,
      title: tariff.title,
      kind: tariff.kind,
    },
  }));

  return session;
};

export const validatePaymentAction = (
  state: SandboxState,
  input: CreatePaymentInput
): number => {
  const usesClientResources =
    ['DEPOSIT', 'BONUS'].includes(input.method) ||
    input.items.some((item) => ['DEPOSIT', 'BONUS'].includes(item.type));
  const client = input.client_uuid
    ? state.clients.find((item) => item.uuid === input.client_uuid)
    : null;
  if (input.client_uuid && !client) {
    throw new SandboxDomainError('Клиент не найден');
  }
  if (usesClientResources && !client) {
    throw new SandboxDomainError('Для платежа необходимо выбрать клиента');
  }
  const total = input.items.reduce((sum, item) => sum + Number(item.sum), 0);
  if (
    !Number.isFinite(total) ||
    total <= 0 ||
    input.items.some(
      (item) =>
        !Number.isFinite(item.amount) ||
        item.amount <= 0 ||
        !Number.isFinite(Number(item.sum)) ||
        Number(item.sum) < 0
    )
  ) {
    throw new SandboxDomainError('Сумма платежа должна быть больше нуля');
  }
  const demand = new Map<number, number>();
  input.items.forEach((item) => {
    if (item.type === 'GOOD' && item.entity_id) {
      demand.set(
        item.entity_id,
        (demand.get(item.entity_id) ?? 0) + item.amount
      );
    }
    if (item.type === 'COMBO' && item.entity_id) {
      const combo = state.combos.find(
        (candidate) => candidate.id === item.entity_id
      );
      if (!combo) throw new SandboxDomainError('Комбо не найдено');
      combo.items
        .filter((part) => part.entity_type === 'GOOD')
        .forEach((part) =>
          demand.set(
            part.entity.id,
            (demand.get(part.entity.id) ?? 0) + part.amount * item.amount
          )
        );
    }
    if (
      ['GOOD', 'SERVICE', 'TARIFF', 'COMBO'].includes(item.type) &&
      !entityFor(state, item.type, item.entity_id)
    ) {
      throw new SandboxDomainError('Позиция продажи не найдена');
    }
  });
  demand.forEach((amount, id) => {
    const good = state.goods.find((item) => item.id === id);
    if (!good || good.amount < amount)
      throw new SandboxDomainError(
        `Недостаточно товара «${good?.title ?? id}»`
      );
  });
  if (input.method === 'DEPOSIT' && client && client.deposit < total)
    throw new SandboxDomainError('Недостаточно средств на депозите');
  if (input.method === 'BONUS' && client && client.bonus < total)
    throw new SandboxDomainError('Недостаточно бонусов');
  if (input.method === 'COMPOSITE') {
    const card = Number(input.card_sum);
    if (!Number.isFinite(card) || card < 0 || card > total) {
      throw new SandboxDomainError(
        'Доля оплаты картой должна быть в пределах суммы платежа'
      );
    }
  }
  return total;
};

export const createPaymentAction = (
  state: SandboxState,
  input: CreatePaymentInput,
  timestamp: string,
  worker: SandboxWorker
): SandboxPayment => {
  const total = validatePaymentAction(state, input);
  const client = input.client_uuid
    ? requireClient(state, input.client_uuid)
    : null;
  const paymentId = state.nextPaymentId++;
  let cardSum = 0;
  let cashSum = 0;
  if (input.method === 'CARD') cardSum = total;
  else if (input.method === 'CASH') cashSum = total;
  else if (input.method === 'COMPOSITE') {
    cardSum = Number(input.card_sum);
    cashSum = total - cardSum;
  }

  const auditPaymentItems = paymentItemsForInput(state, input, paymentId);
  const items = materializePaymentItems(state, input, paymentId);
  items.forEach((item) => {
    if (item.type === 'GOOD' && item.entity_id)
      requireGood(state, item.entity_id).amount -= item.amount;
  });
  const depositAmount = items
    .filter((item) => item.type === 'DEPOSIT')
    .reduce((sum, item) => sum + item.sum, 0);
  const bonusAmount = items
    .filter((item) => item.type === 'BONUS')
    .reduce((sum, item) => sum + item.sum, 0);
  if (client) {
    client.deposit += depositAmount - (input.method === 'DEPOSIT' ? total : 0);
    client.bonus += bonusAmount - (input.method === 'BONUS' ? total : 0);
    client.updated_at = timestamp;
  }

  const payment: SandboxPayment = {
    __typename: 'Payment',
    id: paymentId,
    method: input.method,
    client: client
      ? {
          __typename: 'User',
          uuid: client.uuid,
          nickname: client.nickname,
          phone: client.phone,
        }
      : null,
    sum: total,
    sums: {
      bonus: input.method === 'BONUS' ? total : bonusAmount,
      cashback: 0,
      deposit: input.method === 'DEPOSIT' ? total : 0,
      promo_code: 0,
    },
    bonus: bonusAmount,
    created_at: timestamp,
    is_refunded: false,
    not_refundable: false,
    items,
    card_sum: cardSum,
    cash_sum: cashSum,
    status: 'PAID',
    client_session: null,
  };
  state.payments.unshift(payment);
  const startedSessions: SandboxSession[] = [];
  const tariffItemsByHost = new Map<number, TariffPaymentItem[]>();
  items.forEach((item) => {
    if (item.type !== 'TARIFF' || !item.entity_id || !item.host_id) return;
    const hostId = Number(item.host_id);
    const hostItems = tariffItemsByHost.get(hostId) ?? [];
    hostItems.push(item as TariffPaymentItem);
    tariffItemsByHost.set(hostId, hostItems);
  });
  tariffItemsByHost.forEach((tariffItems) => {
    const session = startPaidTariffSession(
      state,
      tariffItems,
      client,
      payment.id,
      timestamp
    );
    payment.client_session = {
      started_at: session.started_at,
      finished_at: session.finished_at,
      hosts: [
        {
          started_at: session.started_at,
          finished_at: session.finished_at,
          host: {
            alias:
              state.hosts.find((host) => host.id === session.host_id)?.alias ??
              '',
          },
        },
      ],
    };
    startedSessions.push(session);
  });
  state.events.unshift({
    timestamp,
    type: 'PAYMENT_CREATED',
    description: 'Локальный платеж создан в песочнице',
    additional_data: null,
    additional_seconds: null,
    client: client ? eventClient(client) : null,
    worker,
    payment: {
      id: payment.id,
      title: payment.method,
      value: total,
    },
    payment_items: auditPaymentItems,
    deposit: client?.deposit,
  });
  startedSessions.forEach((session) =>
    state.events.unshift(sessionStartedEvent(state, session, timestamp, worker))
  );
  return payment;
};

export const refundPaymentAction = (
  state: SandboxState,
  id: number,
  timestamp: string,
  worker: SandboxWorker
): SandboxPayment => {
  const payment = state.payments.find((item) => item.id === id);
  if (!payment) throw new SandboxDomainError('Платеж не найден');
  if (payment.is_refunded) throw new SandboxDomainError('Платеж уже возвращен');
  if (payment.not_refundable)
    throw new SandboxDomainError('Платеж нельзя вернуть');
  const client = payment.client
    ? state.clients.find((item) => item.uuid === payment.client?.uuid)
    : undefined;
  const depositTopup = payment.items
    .filter((item) => item.type === 'DEPOSIT')
    .reduce((sum, item) => sum + item.sum, 0);
  const bonusTopup = payment.items
    .filter((item) => item.type === 'BONUS')
    .reduce((sum, item) => sum + item.sum, 0);
  const usesClientResources =
    depositTopup > 0 ||
    bonusTopup > 0 ||
    ['DEPOSIT', 'BONUS'].includes(payment.method) ||
    payment.sums.cashback > 0 ||
    payment.sums.promo_code > 0;
  if (usesClientResources && !client) {
    throw new SandboxDomainError('Клиент платежа не найден');
  }
  if (
    client &&
    client.deposit + (payment.method === 'DEPOSIT' ? payment.sum : 0) <
      depositTopup
  )
    throw new SandboxDomainError(
      'Недостаточно депозита для возврата пополнения'
    );
  if (
    client &&
    client.bonus + (payment.method === 'BONUS' ? payment.sum : 0) < bonusTopup
  )
    throw new SandboxDomainError('Недостаточно бонусов для возврата');
  payment.items.forEach((item) => {
    if (item.type === 'GOOD' && item.entity_id)
      requireGood(state, item.entity_id).amount += item.amount;
  });
  if (client) {
    client.deposit +=
      (payment.method === 'DEPOSIT' ? payment.sum : 0) - depositTopup;
    client.bonus += (payment.method === 'BONUS' ? payment.sum : 0) - bonusTopup;
  }
  payment.is_refunded = true;
  state.sessions
    .filter(
      (session) => session.payment_id === id && session.status === 'ACTIVE'
    )
    .forEach((session) => {
      session.status = 'CANCELED';
      session.finished_at = timestamp;
    });
  state.events.unshift({
    timestamp,
    type: 'PAYMENT_REFUND',
    description: 'Платеж возвращен локально',
    additional_data: null,
    additional_seconds: null,
    client: client ? eventClient(client) : null,
    worker,
    payment: { id: payment.id, title: 'Возврат', value: payment.sum },
    deposit: client?.deposit,
  });
  return payment;
};

export const createPendingPostPaymentAction = (
  state: SandboxState,
  session: SandboxSession,
  timestamp: string
): SandboxPayment | null => {
  if (!session.postpaid) return null;
  const client = session.client_uuid
    ? requireClient(state, session.client_uuid)
    : null;
  const tariff = state.tariffs.find(
    (item) => item.id === session.seances[0]?.tariff.id
  );
  if (!tariff) throw new SandboxDomainError('Тариф сессии не найден');
  const host = state.hosts.find((item) => item.id === session.host_id);
  if (!host) throw new SandboxDomainError('Хост сессии не найден');
  const sum = Math.max(session.total_cost, 100);
  const paymentId = state.nextPaymentId++;
  const payment: SandboxPayment = {
    __typename: 'Payment',
    id: paymentId,
    method: 'POSTPAID',
    client: client
      ? {
          __typename: 'User',
          uuid: client.uuid,
          nickname: client.nickname,
          phone: client.phone,
        }
      : null,
    sum,
    sums: { bonus: 0, cashback: 0, deposit: 0, promo_code: 0 },
    bonus: 0,
    created_at: timestamp,
    is_refunded: false,
    not_refundable: false,
    items: [
      {
        id: paymentId * 10,
        amount: 1,
        type: 'TARIFF',
        sum,
        entity_id: tariff.id,
        host_id: host.id,
        entity: {
          __typename: 'Tariff',
          id: tariff.id,
          title: tariff.title,
          cost: tariff.cost,
          disable_sale_receipt_printing: tariff.disable_sale_receipt_printing,
          per_minute: tariff.per_minute,
        },
      },
    ],
    card_sum: 0,
    cash_sum: 0,
    status: 'PENDING',
    client_session: {
      hosts: [
        {
          started_at: session.started_at,
          finished_at: session.finished_at,
          host: { alias: host.alias },
        },
      ],
      started_at: session.started_at,
      finished_at: session.finished_at,
    },
  };
  session.payment_id = payment.id;
  state.payments.unshift(payment);
  return payment;
};

export const processPostPaymentAction = (
  state: SandboxState,
  id: number,
  input: ProcessPostPaymentInput,
  timestamp: string,
  worker: SandboxWorker
): SandboxPayment => {
  const payment = state.payments.find((item) => item.id === id);
  if (!payment || payment.status !== 'PENDING') {
    throw new SandboxDomainError('Ожидающий платеж не найден');
  }
  const client = payment.client
    ? requireClient(state, payment.client.uuid)
    : null;
  if (input.method) {
    const method = input.method;
    if (!['CASH', 'CARD', 'COMPOSITE'].includes(method)) {
      throw new SandboxDomainError('Неподдерживаемый способ постоплаты');
    }
    const cashSum =
      method === 'CASH'
        ? payment.sum
        : method === 'CARD'
        ? 0
        : Number(input.cash_sum);
    if (!Number.isFinite(cashSum) || cashSum < 0 || cashSum > payment.sum) {
      throw new SandboxDomainError('Некорректное распределение постоплаты');
    }
    payment.method = method;
    payment.cash_sum = cashSum;
    payment.card_sum = payment.sum - cashSum;
    payment.status = 'PAID';
  } else {
    if (
      !String(input.comment ?? '').trim() ||
      !String(input.confirm_pass ?? '').trim()
    ) {
      throw new SandboxDomainError('Для отмены нужны комментарий и пароль');
    }
    payment.status = 'UNPAID';
  }
  state.events.unshift({
    timestamp,
    type:
      payment.status === 'PAID' ? 'PAYMENT_CREATED' : 'POST_PAYMENT_CANCELED',
    description:
      payment.status === 'PAID'
        ? 'Постоплата подтверждена локально'
        : 'Постоплата отменена локально',
    additional_data: null,
    additional_seconds: null,
    client: client ? eventClient(client) : null,
    worker,
    payment: { id: payment.id, title: payment.method, value: payment.sum },
    payment_items: paymentItemsForEvent(payment),
    deposit: client?.deposit,
  });
  return payment;
};
